import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import { CloseButton } from '@/ui/buttons/close-button/CloseButton.client'
import { useRef } from 'react'
import { useInteractOutside } from 'react-aria'

import { Link } from '@/ui/links/link/Link.client'

export type CategoryProps = {
  /** The name displayed on the Link */
  name: string

  /** The url inside the href */
  url: string

  /** Open the link in a new tab */
  opensInNewTab?: boolean

  /** The function to call when the link is pressed */
  onPress?: () => void

  /** The event to track */
  trackingData?: TrackingGaEvent
}

export type MenuPopinProps = {
  /** The classNames sent from the parent component */
  className?: string

  /** The title of the sub category */
  title?: CategoryProps

  /** All the sub categories, defined by a name, key and url */
  categories: CategoryProps[]

  /** When the close button is clicked */
  onClose?: () => void

  /** The event to track */
  trackingData?: TrackingGaEvent
}

const MAX_HEIGHT_CLASSES: Record<number, string> = {
  0: 'max-h-[9.25rem]',
  9: 'max-h-[13.875rem] desktop-md:max-h-[11.5625rem]',
  13: 'max-h-[16.1875rem] desktop-md:max-h-[11.5625rem]',
  18: 'max-h-[18.5rem] desktop-md:max-h-[13.875rem]',
} as const

export function MenuPopin({
  title,
  className,
  categories,
  onClose,
  trackingData,
  ...props
}: MenuPopinProps): React.ReactElement {
  const menuPopinRef = useRef<HTMLDivElement>(null)
  useInteractOutside({ ref: menuPopinRef, onInteractOutside: onClose })

  const { t } = useTranslation(['header'])
  const HEADER_MENU_POPIN_PARENT_CATEGORY = t('header:menuPopinParentCategory')
  const count = categories.length

  // Select the last class that matches the item count
  const heightClass =
    Object.entries(MAX_HEIGHT_CLASSES)
      .filter(([minCount]) => count >= Number(minCount))
      .at(-1)?.[1] ?? MAX_HEIGHT_CLASSES[0]

  return (
    <div
      {...props}
      ref={menuPopinRef}
      className={twMerge(
        'relative block w-full max-w-[1376px] overflow-y-auto overflow-x-hidden rounded-sm border-px border-solid border-brand-2-200 bg-white px-4 py-3 pr-6 shadow-xl',
        className,
      )}
      data-testid="menu-popin"
    >
      <div className="w-full">
        {title ? (
          <div data-testid="menu-popin/title">
            <Link
              variant="free"
              underlined="hover"
              className="box-border block max-w-[300px] break-inside-avoid items-center py-0.5"
              href={title.url}
              bold
              opensInNewTab={title.opensInNewTab ?? false}
              onPress={() => {
                title?.onPress?.()
                onClose?.()
              }}
              trackingData={trackingData}
            >
              {title.name || HEADER_MENU_POPIN_PARENT_CATEGORY}
            </Link>
          </div>
        ) : null}
        <div
          className={twMerge(
            'gap-x-1.5 max-h-[9.25rem] columns-3 [column-fill:auto] desktop-md:columns-4',
            heightClass,
          )}
        >
          {categories.map(({ name, url, opensInNewTab, trackingData: categoryTrackingData }) => (
            <Link
              variant="free"
              underlined="hover"
              className="box-border block max-w-[300px] break-inside-avoid items-center py-1"
              key={`${name}-${url}`}
              href={url}
              title={name}
              opensInNewTab={opensInNewTab ?? false}
              onPress={onClose}
              trackingData={categoryTrackingData}
            >
              {name}
            </Link>
          ))}
        </div>
      </div>

      <div className="absolute right-0.5 top-0.5">
        <CloseButton onPress={onClose} />
      </div>
    </div>
  )
}
